/**
 * The core screen: a real tree diff between Blueprint version n and n+1.
 *
 * `BlueprintDiff` arrives as flat paths (`added_paths`, `removed_paths`, `changed[]`).
 * Flattening that into a JSON blob would throw away the one thing review needs — which
 * ad group, which ad, which headline changed. So the paths are parsed into a tree
 * (src/lib/jsonpath.ts), ordered the way the document reads, and rendered with rolled-up
 * counts per branch.
 *
 * Keyboard: the tree is a real `role="tree"`. Up/Down move between visible rows,
 * Right/Left expand/collapse, Enter/Space toggles — the same keys screen readers expect.
 */
import { useMemo, useRef, useState } from 'react'
import { useBlueprintDiff } from '@/api/hooks'
import { buildDiffTree, diffTotals, expandableIds, visibleRows, type ChangeKind } from '@/lib/jsonpath'
import { countCharacters, RSA_DESCRIPTION_MAX_CHARS, RSA_HEADLINE_MAX_CHARS } from '@/lib/limits'
import { formatDiffValue, formatNumber } from '@/lib/format'
import { Badge, Button, Cap, EmptyState, ErrorNotice, Select, Spinner, type Tone } from '@/components/ui'

const KIND_TONE: Record<ChangeKind, Tone> = {
  added: 'ok',
  removed: 'danger',
  changed: 'warn',
  unchanged: 'neutral',
}

const KIND_SIGN: Record<ChangeKind, string> = {
  added: '+',
  removed: '−',
  changed: '~',
  unchanged: '·',
}

/** Headline/description key names are in the path; the limit comes from the frozen schema. */
function limitFor(path: string): { limit: number; label: string } | null {
  if (/headlines\[\d+\]\.text$/.test(path)) return { limit: RSA_HEADLINE_MAX_CHARS, label: 'headline' }
  if (/descriptions\[\d+\]\.text$/.test(path)) return { limit: RSA_DESCRIPTION_MAX_CHARS, label: 'description' }
  return null
}

export function DiffPanel({ blueprintId, against }: { blueprintId: string; against?: string }) {
  const diff = useBlueprintDiff(blueprintId, against)
  // `null` means "untouched"; an empty Set means the user collapsed everything on
  // purpose, which must stick.
  const [expanded, setExpanded] = useState<Set<string> | null>(null)
  const [hideUnchanged, setHideUnchanged] = useState(true)
  const [filter, setFilter] = useState<ChangeKind | 'all'>('all')
  const rowRefs = useRef(new Map<string, HTMLDivElement>())

  const tree = useMemo(() => (diff.data ? buildDiffTree(diff.data) : null), [diff.data])
  const totals = useMemo(() => (diff.data ? diffTotals(diff.data) : null), [diff.data])

  const effectiveExpanded = useMemo(() => {
    if (expanded !== null) return expanded
    if (!tree) return new Set<string>()
    // First paint: open every branch that contains a change, so review starts with the
    // whole change set visible rather than a wall of collapsed nodes.
    return new Set(expandableIds(tree).slice(0, 40))
  }, [expanded, tree])

  const rows = useMemo(
    () => (tree ? visibleRows(tree, { expanded: effectiveExpanded, hideUnchanged, filter }) : []),
    [tree, effectiveExpanded, hideUnchanged, filter],
  )

  function toggle(id: string) {
    setExpanded(() => {
      const next = new Set(effectiveExpanded)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function expandAll() {
    setExpanded(new Set(expandableIds(tree!)))
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>, index: number) {
    const row = rows[index]
    if (!row) return
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      const nextIndex = event.key === 'ArrowDown' ? index + 1 : index - 1
      const nextRow = rows[nextIndex]
      if (nextRow) rowRefs.current.get(nextRow.node.id)?.focus()
    } else if (event.key === 'ArrowRight' && row.hasChildren && !row.expanded) {
      event.preventDefault()
      toggle(row.node.id)
    } else if (event.key === 'ArrowLeft' && row.hasChildren && row.expanded) {
      event.preventDefault()
      toggle(row.node.id)
    } else if (event.key === 'Home') {
      event.preventDefault()
      const first = rows[0]
      if (first) rowRefs.current.get(first.node.id)?.focus()
    } else if (event.key === 'End') {
      event.preventDefault()
      const last = rows.at(-1)
      if (last) rowRefs.current.get(last.node.id)?.focus()
    }
  }

  if (diff.isLoading) return <Spinner label="Diffing versions…" />
  if (diff.isError) return <ErrorNotice error={diff.error} />
  if (!diff.data || !tree || !totals) return null

  return (
    <div className="flex flex-col gap-3" data-testid="diff-panel">
      <div className="flex flex-wrap items-center gap-2.5">
        <Badge tone="neutral" testId="diff-versions">
          v{diff.data.from_version} → v{diff.data.to_version}
        </Badge>
        <Badge tone="warn" testId="diff-changed-count">
          {formatNumber(totals.changed)} changed
        </Badge>
        <Badge tone="ok" testId="diff-added-count">
          {formatNumber(totals.added)} added
        </Badge>
        <Badge tone="danger" testId="diff-removed-count">
          {formatNumber(totals.removed)} removed
        </Badge>
        <span className="text-[12px] text-mist-400">{diff.data.summary}</span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5 text-[12px] text-mist-300">
          <input
            type="checkbox"
            data-testid="hide-unchanged"
            checked={hideUnchanged}
            onChange={(event) => setHideUnchanged(event.target.checked)}
            className="size-3.5 accent-forge-400"
          />
          Hide unchanged nodes
        </label>
        <Select
          aria-label="Filter changes by kind"
          data-testid="diff-filter"
          value={filter}
          onChange={(event) => setFilter(event.target.value as ChangeKind | 'all')}
          className="w-[168px] py-1 text-[12px]"
        >
          <option value="all">All changes</option>
          <option value="changed">Changed only</option>
          <option value="added">Added only</option>
          <option value="removed">Removed only</option>
        </Select>
        <div className="ml-auto flex items-center gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            onClick={expandAll}
            disabled={rows.length === 0}
          >
            Expand all
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setExpanded(new Set<string>())}
            disabled={rows.length === 0}
          >
            Collapse all
          </Button>
        </div>
      </div>

      {totals.total === 0 ? (
        <EmptyState
          title="No changes between these versions"
          body="This version is byte-identical to the one it is compared against. Nothing to review — and nothing extra to push."
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nothing matches the current filter"
          body="Clear the filter or tick “Hide unchanged nodes” off to see the surrounding structure."
        />
      ) : (
        <div
          role="tree"
          aria-label={`Blueprint changes, version ${diff.data.from_version} to ${diff.data.to_version}`}
          data-testid="diff-tree"
          className="max-h-[560px] overflow-auto rounded-lg border border-ink-750 bg-ink-850/40"
        >
          {rows.map((row, index) => {
            const { node } = row
            const nodeTotal = node.counts.added + node.counts.removed + node.counts.changed
            const kind = node.entry?.kind ?? (nodeTotal > 0 ? node.kind : 'unchanged')
            const toggleable = row.hasChildren
            const isLeaf = !toggleable
            const before = node.entry?.before
            const after = node.entry?.after
            const limit = node.entry ? limitFor(node.entry.path) : null
            return (
              <div
                key={node.id}
                ref={(element) => {
                  if (element) rowRefs.current.set(node.id, element)
                  else rowRefs.current.delete(node.id)
                }}
                role="treeitem"
                aria-level={node.depth}
                aria-expanded={toggleable ? row.expanded : undefined}
                aria-selected={undefined}
                tabIndex={index === 0 ? 0 : -1}
                data-testid="diff-row"
                data-path={node.id}
                data-kind={node.entry?.kind ?? 'container'}
                data-state={row.expanded ? 'expanded' : toggleable ? 'collapsed' : 'leaf'}
                onClick={() => toggleable && toggle(node.id)}
                onKeyDown={(event) => {
                  if (toggleable && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault()
                    toggle(node.id)
                    return
                  }
                  onKeyDown(event, index)
                }}
                className="group flex cursor-default items-start gap-2 border-b border-ink-800/70 px-2 py-1.5 last:border-b-0 hover:bg-ink-800/60 focus:bg-ink-800"
                style={{ paddingLeft: `${8 + (node.depth - 1) * 16}px` }}
              >
                <span aria-hidden="true" className="mt-[3px] w-3 shrink-0 text-center text-[10px] text-mist-500">
                  {toggleable ? (row.expanded ? '▾' : '▸') : ''}
                </span>
                <span
                  aria-hidden="true"
                  className={`num mt-[1px] w-3 shrink-0 text-center text-[12px] font-bold ${
                    kind === 'added'
                      ? 'text-signal-ok'
                      : kind === 'removed'
                        ? 'text-signal-danger'
                        : kind === 'changed'
                          ? 'text-signal-warn'
                          : 'text-mist-500'
                  }`}
                >
                  {KIND_SIGN[kind]}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-baseline gap-2">
                    <span className="num text-[12.5px] text-mist-100">{node.label}</span>
                    {node.entry ? (
                      <Badge tone={KIND_TONE[node.entry.kind]}>{node.entry.kind}</Badge>
                    ) : nodeTotal > 0 ? (
                      <span className="text-[11px] text-mist-500">
                        {[
                          node.counts.changed ? `${node.counts.changed} changed` : null,
                          node.counts.added ? `${node.counts.added} added` : null,
                          node.counts.removed ? `${node.counts.removed} removed` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </span>
                    ) : null}
                    {isLeaf && row.hiddenChanges === 0 && nodeTotal === 0 && filter !== 'all' ? (
                      <span className="text-[11px] text-mist-500">unchanged</span>
                    ) : null}
                  </span>

                  {node.entry ? (
                    <span className="mt-1 flex flex-wrap items-center gap-2" data-testid="diff-values">
                      {node.entry.kind === 'changed' ? (
                        <>
                          <span className="num max-w-[36ch] truncate rounded border border-ink-700 bg-ink-900 px-1.5 py-[2px] text-[11.5px] text-mist-400 line-through decoration-signal-danger/50">
                            {formatDiffValue(before).text}
                          </span>
                          <span aria-hidden="true" className="text-mist-500">
                            →
                          </span>
                          <span className="num max-w-[42ch] truncate rounded border border-signal-ok/30 bg-signal-ok/5 px-1.5 py-[2px] text-[11.5px] text-mist-100">
                            {formatDiffValue(after).text}
                          </span>
                        </>
                      ) : node.entry.kind === 'added' ? (
                        <span className="num max-w-[48ch] truncate rounded border border-signal-ok/30 bg-signal-ok/5 px-1.5 py-[2px] text-[11.5px] text-mist-100">
                          {formatDiffValue(after).text}
                        </span>
                      ) : (
                        <span className="num max-w-[48ch] truncate rounded border border-signal-danger/30 bg-signal-danger/5 px-1.5 py-[2px] text-[11.5px] text-mist-400">
                          {formatDiffValue(before).text}
                        </span>
                      )}
                    </span>
                  ) : null}
                </span>

                {limit && typeof after === 'string' ? (
                  <span
                    className={`num shrink-0 self-center text-[11px] ${
                      countCharacters(after) > limit.limit ? 'text-signal-danger' : 'text-mist-500'
                    }`}
                    title={`${limit.label} limit is ${limit.limit} counted characters`}
                  >
                    {countCharacters(after)}/{limit.limit}
                  </span>
                ) : null}
                {row.hiddenChanges > 0 ? (
                  <span className="shrink-0 self-center text-[11px] text-mist-500">
                    {row.hiddenChanges} below
                  </span>
                ) : null}
              </div>
            )
          })}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Cap>Reading order</Cap>
        <span className="text-[11.5px] text-mist-500">
          Paths follow the Blueprint shape (campaigns → ad groups → ads/keywords → assets), not
          alphabetical order, so the diff reads like the document.
        </span>
      </div>
    </div>
  )
}
